import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface SessionParticipant {
  id: string;
  user_id: string;
  role: 'owner' | 'contributor' | 'reviewer' | 'viewer';
  created_at: string;
  name: string | null;
  email: string;
  avatar_url: string | null;
}

export function useSessionParticipants(sessionId: string | undefined) {
  const { user } = useAuth();
  const [participants, setParticipants] = useState<SessionParticipant[]>([]);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('decision_participants')
        .select(`
          id,
          user_id,
          role,
          created_at,
          profiles!decision_participants_user_id_fkey (
            name,
            email,
            avatar_url
          )
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      const mapped: SessionParticipant[] = (data || []).map((p: any) => ({
        id: p.id,
        user_id: p.user_id,
        role: p.role,
        created_at: p.created_at,
        name: p.profiles?.name || null,
        email: p.profiles?.email || '',
        avatar_url: p.profiles?.avatar_url || null,
      }));

      setParticipants(mapped);

      if (user) {
        const me = mapped.find(p => p.user_id === user.id);
        setMyRole(me?.role || null);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addParticipant = async (userId: string, role: 'contributor' | 'reviewer' | 'viewer') => {
    if (!sessionId || !user) throw new Error('Missing session or user');

    const { error } = await supabase
      .from('decision_participants')
      .insert({
        session_id: sessionId,
        user_id: userId,
        role,
        added_by: user.id,
      });

    if (error) throw error;
    await refresh();
  };

  const updateRole = async (participantId: string, newRole: 'contributor' | 'reviewer' | 'viewer') => {
    const { error } = await supabase
      .from('decision_participants')
      .update({ role: newRole })
      .eq('id', participantId);

    if (error) throw error;
    await refresh();
  };

  const removeParticipant = async (participantId: string) => {
    const { error } = await supabase
      .from('decision_participants')
      .delete()
      .eq('id', participantId);

    if (error) throw error;
    await refresh();
  };

  return {
    participants,
    myRole,
    isOwner: myRole === 'owner',
    loading,
    error,
    refresh,
    addParticipant,
    updateRole,
    removeParticipant,
  };
}
